/**
 * Right-side drawer for viewing and editing a compliance OCCURRENCE.
 * Assignees + admins can edit; other users see read-only.
 */
import React, { useState } from 'react';
import { X, Upload, Trash2, ExternalLink, Loader, CheckCircle, UserPlus } from 'lucide-react';
import { complianceAPI } from '../../services/api';
import RichTextEditor from '../email/RichTextEditor';
import { useDateFormat } from '../../hooks/useDateFormat';

const OccurrenceDrawer = ({ occurrence, onClose, onChanged, isAdmin, currentUserId, team = [] }) => {
  const canEdit = isAdmin || occurrence.assignee_id === currentUserId;
  const fmtDate = useDateFormat();
  const [wipStatus, setWipStatus] = useState(occurrence.wip_status || 'Not Started');
  const [completionDate, setCompletionDate] = useState(occurrence.completion_date || '');
  const [remarksHtml, setRemarksHtml] = useState(occurrence.remarks_html || '');
  const [dueDate, setDueDate] = useState(occurrence.due_date || '');
  const [assigneeId, setAssigneeId] = useState(occurrence.assignee_id || '');
  const [customValues, setCustomValues] = useState({ ...(occurrence.custom_values || {}) });
  const [documents, setDocuments] = useState(occurrence.documents || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);
  const [showReassign, setShowReassign] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const [reassignMsg, setReassignMsg] = useState('');
  const [reassignSaving, setReassignSaving] = useState(false);

  const canRequestReassign = !isAdmin && occurrence.assignee_id === currentUserId;
  const isUnassigned = !occurrence.assignee_id;
  const [claiming, setClaiming] = useState(false);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      await complianceAPI.claimOccurrence(occurrence.id);
      onChanged?.();
      onClose?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to self-assign');
    } finally {
      setClaiming(false);
    }
  };

  const customFields = occurrence.custom_fields || [];

  const handleSave = async () => {
    if (!canEdit) return;
    if (wipStatus === 'Not Applicable' && !remarksHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()) {
      alert('Remarks are mandatory when marking a compliance as Not Applicable.');
      return;
    }
    setSaving(true);
    setSavedMsg(null);
    try {
      const payload = {
        wip_status: wipStatus,
        completion_date: completionDate || null,
        remarks_html: remarksHtml,
        custom_values: customValues,
      };
      if (isAdmin) {
        payload.assignee_id = assigneeId || null;
        // For event-based, admins can also set the actual due date
        if (occurrence.compliance_type === 'event_based' && dueDate) {
          payload.due_date = dueDate;
        }
      }
      const res = await complianceAPI.updateOccurrence(occurrence.id, payload);
      setSavedMsg('Saved');
      onChanged?.();
      // Refresh local values from server (esp. completion_date)
      setCompletionDate(res.data?.completion_date || '');
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
      setTimeout(() => setSavedMsg(null), 2000);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await complianceAPI.uploadDocument(occurrence.id, file);
      setDocuments(d => [...d, res.data]);
      onChanged?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"?`)) return;
    try {
      await complianceAPI.deleteDocument(occurrence.id, doc.id);
      setDocuments(d => d.filter(x => x.id !== doc.id));
      onChanged?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  const previewUrl = (doc) => {
    const url = complianceAPI.previewDocumentUrl(occurrence.id, doc.id);
    // Attach token via header would require fetch; return URL for anchor + inject via new fetch
    return url;
  };

  const handlePreview = async (doc) => {
    // Fetch as blob (auth header via axios interceptor), then open in new tab
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(previewUrl(doc), { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      alert('Preview failed');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch justify-end" data-testid="occurrence-drawer">
      <div className="bg-white shadow-2xl w-full sm:max-w-lg h-full overflow-y-auto flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">{occurrence.item_name}</h3>
            <div className="text-xs text-gray-500 mt-0.5">
              {occurrence.fund_name} · <span className="capitalize">{(occurrence.compliance_type || '').replace('_', '-')}</span>
              {occurrence.event_name && <span className="ml-1 text-blue-600">· Event: {occurrence.event_name}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="drawer-close"><X size={20} /></button>
        </div>

        <div className="px-5 py-4 space-y-4 flex-1">
          {isUnassigned && (
            <div className="bg-blue-50 border border-blue-200 px-3 py-2 rounded flex items-center justify-between gap-2">
              <span className="text-xs text-blue-800">Nobody owns this yet — you can pick it up.</span>
              <button
                onClick={handleClaim}
                disabled={claiming}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300 whitespace-nowrap"
                data-testid="drawer-claim-btn"
              >
                {claiming ? <Loader size={12} className="animate-spin" /> : <UserPlus size={12} />}
                Assign to me
              </button>
            </div>
          )}
          {!canEdit && !isUnassigned && (
            <div className="bg-amber-50 border border-amber-200 text-xs text-amber-800 px-3 py-2 rounded">
              Read-only — this compliance is not assigned to you. Ask an admin to reassign.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs font-medium text-gray-500">Due Date</div>
              {isAdmin && occurrence.compliance_type === 'event_based' ? (
                <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                       className="w-full border rounded px-2 py-1 text-sm mt-1"
                       data-testid="drawer-due-date" />
              ) : (
                <div className="mt-1 font-mono">{occurrence.due_date ? fmtDate(occurrence.due_date) : <span className="italic text-gray-400">event pending</span>}</div>
              )}
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500">Internal Due</div>
              <div className="mt-1 font-mono text-gray-600">{occurrence.internal_due_date ? fmtDate(occurrence.internal_due_date) : '—'}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500">Section</div>
              <div className="mt-1 text-gray-700">{occurrence.section_name || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500">Assignee</div>
              {isAdmin ? (
                <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm mt-1"
                        data-testid="drawer-assignee">
                  <option value="">— Unassigned —</option>
                  {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              ) : (
                <div className="mt-1">
                  {occurrence.assignee_name
                    ? <span className="text-gray-700">{occurrence.assignee_name}</span>
                    : <span className="text-xs px-2 py-0.5 rounded border bg-red-100 text-red-800 border-red-300 font-medium">Unassigned</span>}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">WIP Status</label>
              <select value={wipStatus} onChange={e => setWipStatus(e.target.value)}
                      disabled={!canEdit}
                      className="w-full border rounded px-2 py-1.5 text-sm mt-1 disabled:bg-gray-100"
                      data-testid="drawer-wip-status">
                <option>Not Started</option>
                <option>Active</option>
                <option>Completed</option>
                <option>Not Applicable</option>
              </select>
              {wipStatus === 'Not Applicable' && (
                <p className="text-[11px] text-amber-700 mt-1" data-testid="na-remarks-hint">
                  Remarks are mandatory — explain why this period is not applicable.
                </p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Completion Date</label>
              <input type="date" value={completionDate} onChange={e => setCompletionDate(e.target.value)}
                     disabled={!canEdit || wipStatus !== 'Completed'}
                     className="w-full border rounded px-2 py-1.5 text-sm mt-1 disabled:bg-gray-100"
                     data-testid="drawer-completion-date" />
            </div>
          </div>

          {customFields.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">Custom Fields</div>
              <div className="grid grid-cols-1 gap-2">
                {customFields.map(cf => (
                  <div key={cf.key}>
                    <label className="text-xs text-gray-600">{cf.label}</label>
                    {cf.field_type === 'yes_no' ? (
                      <select value={customValues[cf.key] || ''}
                              onChange={e => setCustomValues(v => ({ ...v, [cf.key]: e.target.value }))}
                              disabled={!canEdit}
                              className="w-full border rounded px-2 py-1 text-sm disabled:bg-gray-100">
                        <option value="">—</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    ) : (
                      <input type={cf.field_type === 'number' ? 'number' : cf.field_type === 'date' ? 'date' : 'text'}
                             value={customValues[cf.key] ?? ''}
                             onChange={e => setCustomValues(v => ({ ...v, [cf.key]: e.target.value }))}
                             disabled={!canEdit}
                             className="w-full border rounded px-2 py-1 text-sm disabled:bg-gray-100"
                             data-testid={`drawer-custom-${cf.key}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Remarks</label>
            <RichTextEditor value={remarksHtml} onChange={setRemarksHtml} placeholder="Add remarks…" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-700">Supporting Documents</label>
              {canEdit && (
                <label className="inline-flex items-center gap-1 cursor-pointer text-xs text-blue-600 hover:text-blue-800">
                  {uploading ? <Loader size={12} className="animate-spin" /> : <Upload size={12} />}
                  {uploading ? 'Uploading…' : 'Upload'}
                  <input type="file" onChange={handleUpload} className="hidden" data-testid="drawer-upload-input" />
                </label>
              )}
            </div>
            {documents.length === 0 ? (
              <div className="text-xs text-gray-500 italic">No documents uploaded.</div>
            ) : (
              <ul className="border rounded divide-y" data-testid="drawer-doc-list">
                {documents.map(d => (
                  <li key={d.id} className="flex items-center justify-between px-2 py-1.5 text-sm">
                    <div className="truncate flex-1">
                      <div className="truncate font-medium text-gray-900">{d.filename}</div>
                      <div className="text-xs text-gray-500">{d.content_type} · {(d.size / 1024).toFixed(1)} KB</div>
                    </div>
                    <button onClick={() => handlePreview(d)} title="Preview" className="text-blue-600 hover:text-blue-800 mx-2" data-testid={`preview-${d.id}`}>
                      <ExternalLink size={14} />
                    </button>
                    {canEdit && (
                      <button onClick={() => handleDeleteDoc(d)} title="Delete" className="text-red-600 hover:text-red-800" data-testid={`delete-doc-${d.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
          {savedMsg && <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={12} /> {savedMsg}</span>}
          {canRequestReassign && (
            <button onClick={() => setShowReassign(true)}
                    className="px-3 py-1.5 text-sm border border-violet-600 text-violet-700 rounded hover:bg-violet-50 inline-flex items-center gap-1"
                    data-testid="drawer-request-reassign">
              <UserPlus size={12} /> Request Reassign
            </button>
          )}
          <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded" data-testid="drawer-cancel">Close</button>
          {canEdit && (
            <button onClick={handleSave} disabled={saving}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="drawer-save">
              {saving && <Loader size={12} className="animate-spin" />}
              Save
            </button>
          )}
        </div>

        {showReassign && (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="reassign-modal">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between px-5 py-3 border-b">
                <h4 className="font-semibold text-sm inline-flex items-center gap-1.5"><UserPlus size={14} /> Request Reassignment</h4>
                <button onClick={() => setShowReassign(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
              </div>
              <div className="px-5 py-4 space-y-3">
                <p className="text-xs text-gray-600">
                  The person you pick will be notified by email. The compliance stays with you until they accept.
                </p>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Reassign to</label>
                  <select value={reassignTo} onChange={e => setReassignTo(e.target.value)}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                          data-testid="reassign-to">
                    <option value="">Pick a team member…</option>
                    {team.filter(m => m.id !== currentUserId).map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Note (optional)</label>
                  <textarea value={reassignMsg} onChange={e => setReassignMsg(e.target.value)}
                            rows={3}
                            placeholder="e.g. I'm on leave next week — could you cover this?"
                            className="w-full border rounded px-2 py-1.5 text-sm"
                            data-testid="reassign-msg" />
                </div>
              </div>
              <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
                <button onClick={() => setShowReassign(false)} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
                <button onClick={async () => {
                          if (!reassignTo) return;
                          setReassignSaving(true);
                          try {
                            await complianceAPI.createReassignment({
                              occurrence_id: occurrence.id,
                              to_user_id: reassignTo,
                              message: reassignMsg || null,
                            });
                            setShowReassign(false);
                            setReassignTo(''); setReassignMsg('');
                            setSavedMsg('Reassignment request sent');
                            onChanged?.();
                            setTimeout(() => setSavedMsg(null), 3000);
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Request failed');
                          } finally {
                            setReassignSaving(false);
                          }
                        }}
                        disabled={!reassignTo || reassignSaving}
                        className="px-3 py-1.5 bg-violet-600 text-white rounded text-sm hover:bg-violet-700 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid="reassign-submit">
                  {reassignSaving && <Loader size={12} className="animate-spin" />}
                  Send Request
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OccurrenceDrawer;
